import {
  DashboardOutlined,
  MobileOutlined,
  BarChartOutlined,
  RiseOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  ScheduleOutlined,
  FolderOutlined,
  FileOutlined,
  FileTextOutlined,
  ProjectOutlined,
  ShareAltOutlined,
  SecurityScanOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  UsergroupAddOutlined,
  CalendarOutlined,
  UserOutlined,
  PieChartOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  ToolOutlined,
  PoundCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from "@ant-design/icons";
import { Layout, Menu } from "antd";
import classNames from "classnames";
import React from "react";
import { FaRegCheckCircle } from "react-icons/fa";
import { GrUserWorker } from "react-icons/gr";
import { Link, useNavigate, useLocation } from "react-router-dom";

import logoImage from "../../assets/mnj-logo.jpg";
import { canAccessProjectTeamPage } from "../../constants/users";
import { useUser } from "../../contexts/UserContext";
import { NavigationItem } from "../../types";
import { navigationItems, bottomNavigationItems } from "../../utils/navigation";

const { Sider } = Layout;

const iconMap: { [key: string]: React.ReactNode } = {
  dashboard: <DashboardOutlined />,
  mobile: <MobileOutlined />,
  analytics: <BarChartOutlined />,
  trending: <RiseOutlined />,
  map: <EnvironmentOutlined />,
  categories: <AppstoreOutlined />,
  schedule: <ScheduleOutlined />,
  folder: <FolderOutlined />,
  file: <FileOutlined />,
  template: <FileTextOutlined />,
  progress: <ProjectOutlined />,
  workflow: <ShareAltOutlined />,
  security: <SecurityScanOutlined />,
  resources: <UsergroupAddOutlined />,
  team: <TeamOutlined />,
  calendar: <CalendarOutlined />,
  users: <UserOutlined />,
  chart: <PieChartOutlined />,
  alert: <ExclamationCircleOutlined />,
  cog: <SettingOutlined />,
  clock: <ClockCircleOutlined />,
  labour: <LineChartOutlined />,
  plant: <ToolOutlined />,
  dollar: <PoundCircleOutlined />,
  worker: <GrUserWorker />,
  approval: <FaRegCheckCircle />
};

const getIconByType = (iconType: string) =>
  iconMap[iconType] || <FileOutlined />;

/** antd rebuilds every menu item on a new `items` array, so build both lists once. */
const mainMenuItems = navigationItems.map((item) => ({
  key: item.id,
  label: item.label,
  icon: getIconByType(item.icon)
}));

const toMenuItem = (item: NavigationItem) => ({
  key: item.id,
  label: item.label,
  icon: getIconByType(item.icon),
  style: {
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    margin: "0px"
  }
});

const selectedKeyPrefixes = [
  "dashboard",
  "forecast-budget",
  "forecasts",
  "forecasting",
  "project-team",
  "whats-on-site",
  "whos-on-site",
  "approvals",
  "documents"
];

const getSelectedKeys = (pathname: string) => {
  const match = selectedKeyPrefixes.find((prefix) => pathname.includes(prefix));
  return match ? [match] : [];
};

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  toggleCollapsed: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  collapsed,
  toggleCollapsed
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();

  const canAccessProjectTeam = canAccessProjectTeamPage(user?.roleDisplayName);

  const visibleBottomNavigationItems = React.useMemo(
    () =>
      bottomNavigationItems.filter(
        (item) => item.id !== "project-team" || canAccessProjectTeam
      ),
    [canAccessProjectTeam]
  );

  const bottomMenuItems = React.useMemo(
    () => visibleBottomNavigationItems.map(toMenuItem),
    [visibleBottomNavigationItems]
  );

  const selectedKeys = React.useMemo(
    () => getSelectedKeys(location.pathname),
    [location.pathname]
  );

  const handleMenuClick = React.useCallback(
    (e: { key: string }) => {
      // Find the navigation item with this key
      const findItemByKey = (
        items: NavigationItem[],
        key: string
      ): NavigationItem | null => {
        for (const item of items) {
          if (item.id === key) return item;
          if (item.children) {
            const found = findItemByKey(item.children, key);
            if (found) return found;
          }
        }
        return null;
      };

      const item = findItemByKey(
        [...navigationItems, ...bottomNavigationItems],
        e.key
      );
      if (item?.path) {
        navigate(item.path);
        // Only close sidebar on mobile after navigation
        if (window.innerWidth < 1024) {
          onClose();
        }
      }
    },
    [navigate, onClose]
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            inset: "0",
            backgroundColor: "var(--color-overlay)",
            zIndex: "var(--z-modal-backdrop)"
          }}
          className="lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <Sider
        width={220}
        collapsible
        collapsed={collapsed}
        trigger={null}
        className={`fixed top-0 left-0 h-screen lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{
          zIndex: "var(--z-modal)",
          backgroundColor: "#0a2642",
          borderRight: `1px solid rgba(255, 255, 255, 0.1)`
        }}
        theme="light"
      >
        <div
          style={{
            padding: "var(--space-6)",
            borderBottom: `1px solid rgba(255, 255, 255, 0.1)`
          }}
        >
          {collapsed ? (
            <div className="flex justify-center items-center">
              <span
                role="button"
                onClick={toggleCollapsed}
                className="cursor-pointer text-white"
              >
                <MenuUnfoldOutlined style={{ fontSize: 24 }} />
              </span>
            </div>
          ) : (
            <div className="flex justify-between items-center gap-x-2">
              <Link to="/" className={classNames(collapsed && "hidden")}>
                <img
                  src={logoImage}
                  alt="M&J Logo"
                  style={{
                    height: "64px",
                    width: "auto",
                    objectFit: "contain",
                    borderRadius: "var(--radius-small)",
                    display: "block"
                  }}
                />
              </Link>
              <span
                role="button"
                onClick={toggleCollapsed}
                className="cursor-pointer text-white"
              >
                <MenuFoldOutlined style={{ fontSize: 24 }} />
              </span>
            </div>
          )}
        </div>

        <div
          className="sidebar-scrollable flex flex-col"
          style={{
            height: collapsed ? "calc(100vh - 50px)" : "calc(100vh - 90px)",
            overflowY: "auto",
            overflowX: "hidden"
          }}
        >
          <Menu
            mode="inline"
            items={mainMenuItems}
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
            className="sidebar-menu-dark"
            style={{
              backgroundColor: "transparent",
              fontSize: "var(--font-size-sm)",
              fontFamily: "var(--font-family)",
              color: "#ffffff",
              border: "none"
            }}
          />
          <div className="flex-1" />
          <Menu
            mode="inline"
            items={bottomMenuItems}
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
            className="sidebar-menu-dark"
            style={{
              backgroundColor: "transparent",
              fontSize: "var(--font-size-sm)",
              fontFamily: "var(--font-family)",
              color: "#ffffff",
              border: "none"
            }}
          />
        </div>
      </Sider>
    </>
  );
};

export default React.memo(Sidebar);
